import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import {
  clearRegistrationRuntime,
  setRegistrationRuntime,
} from "@profile/features/registration/runtime";
import type {
  TrackDownloadAppEventFn,
  TrackRegistrationEventFn,
  TrackRegistrationValuePropEventFn,
} from "../features/registration/analytics";

export interface RegistrationConfig {
  apiBaseUrlV2: string;
  apiBaseUrl: string;
  qualtricsUrl?: string;
  qualtricsAPIKey?: string;
  registrationNpsSurveyId?: string;
  cdnRootUrl?: string;
  mobileAppDownloadUrl?: string;
  baseUrl?: string;
}

export interface RegistrationProviderValue {
  config: RegistrationConfig;
  trackRegistrationEvent: TrackRegistrationEventFn;
  trackRegistrationValuePropEvent: TrackRegistrationValuePropEventFn;
  trackDownloadAppEvent: TrackDownloadAppEventFn;
  syncAnalyticsEmailId?: (email: string) => void;
  syncAnalyticsUserUUID?: (userUUID: string) => void;
  buildImageUrl: (relativePath: string) => string;
  setApiBaseUrl: (url: string) => void;
  getApiBaseUrl: () => string | undefined;
}

const RegistrationConfigContext = createContext<RegistrationProviderValue | null>(null);

let providerMountCount = 0;

export interface RegistrationProviderProps {
  config: RegistrationConfig;
  trackRegistrationEvent: TrackRegistrationEventFn;
  trackRegistrationValuePropEvent: TrackRegistrationValuePropEventFn;
  trackDownloadAppEvent: TrackDownloadAppEventFn;
  syncAnalyticsEmailId?: (email: string) => void;
  syncAnalyticsUserUUID?: (userUUID: string) => void;
  buildImageUrl: (relativePath: string) => string;
  setApiBaseUrl?: (url: string) => void;
  getApiBaseUrl?: () => string | undefined;
  children: ReactNode;
}

export function RegistrationProvider({
  config,
  trackRegistrationEvent,
  trackRegistrationValuePropEvent,
  trackDownloadAppEvent,
  syncAnalyticsEmailId,
  syncAnalyticsUserUUID,
  buildImageUrl,
  setApiBaseUrl,
  getApiBaseUrl,
  children,
}: Readonly<RegistrationProviderProps>) {
  const value: RegistrationProviderValue = useMemo(() => ({
    config,
    trackRegistrationEvent,
    trackRegistrationValuePropEvent,
    trackDownloadAppEvent,
    syncAnalyticsEmailId,
    syncAnalyticsUserUUID,
    buildImageUrl,
    setApiBaseUrl: setApiBaseUrl ?? (() => {}),
    getApiBaseUrl: getApiBaseUrl ?? (() => undefined),
  }), [
    config,
    trackRegistrationEvent,
    trackRegistrationValuePropEvent,
    trackDownloadAppEvent,
    syncAnalyticsEmailId,
    syncAnalyticsUserUUID,
    buildImageUrl,
    setApiBaseUrl,
    getApiBaseUrl,
  ]);

  // Children call runtime helpers (e.g. buildImageUrl) during render — init before JSX.
  setRegistrationRuntime(value);

  useEffect(() => {
    providerMountCount += 1;
    return () => {
      providerMountCount -= 1;
      if (providerMountCount === 0) {
        clearRegistrationRuntime();
      }
    };
  }, []);

  return (
    <RegistrationConfigContext.Provider value={value}>
      {children}
    </RegistrationConfigContext.Provider>
  );
}

export function useRegistrationProvider(): RegistrationProviderValue {
  const ctx = useContext(RegistrationConfigContext);
  if (!ctx) {
    throw new Error("useRegistrationProvider must be used within RegistrationProvider");
  }
  return ctx;
}

/** Access registration config values */
export function useRegistrationConfig(): RegistrationConfig {
  return useRegistrationProvider().config;
}

/** Shorthand for config.env-style access */
export function useRegistrationEnv(key: keyof RegistrationConfig): string {
  const { config } = useRegistrationProvider();
  const value = config[key];
  return value == null ? "" : String(value);
}

export {
  RegistrationEvent,
  RegistrationValuePropEvent,
  DownloadAppEvent,
} from "../features/registration/analytics";
